import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ESFORCO_PADRAO_OPENCODE, provedorDoModelo, ROTAS, type ModeloOpencode } from '@remoto/shared';
import { provedorOpencodeNaoTratado, type ProvedorOpencode } from '@remoto/protocolo';
import { chamar } from '../../api';
import { mensagemDoErro } from '../../erros';
import type { Sessao } from '../../sessoes/conversa';
import { rotuloDoEsforco } from '../../sessoes/criacao-por-motor';
import { usarCatalogo } from '../../configuracoes/dados';
import { EscolhaDeModelo } from '../../configuracoes/EscolhaDeModelo';
import { esforcoAposTrocarModelo, opcoesDeEsforco } from '../../configuracoes/regras';
import { Modal } from '../Modal';
import { CampoDeEscolha } from '../campos';

/**
 * O que a pílula de modelo de uma conversa do OpenCode mostra fechada.
 *
 * O id CRU do modelo (id feio é melhor que nome inventado) e o esforço com
 * nome de gente: `padrao` vira "padrão do provedor".
 */
export function rotuloDoModeloOpencode(sessao: Pick<Sessao, 'modelo' | 'esforco'>): string {
  return (
    (sessao.modelo ?? 'modelo') + ' · ' + rotuloDoEsforco(sessao.esforco ?? ESFORCO_PADRAO_OPENCODE)
  );
}

/**
 * Onde o custo de cada resposta aparece, e o que ele é, pelo provedor da
 * conversa (F2, D45): o cobrado no OpenRouter, o equivalente de tabela no
 * NanoGPT.
 */
function fraseDoCusto(provedor: ProvedorOpencode | null): string {
  if (provedor === null) return 'O custo de cada resposta aparece no fim dela.';
  switch (provedor) {
    case 'nanogpt':
      return 'O custo de cada resposta aparece no fim dela, em preço de tabela do NanoGPT: é o equivalente, não o cobrado.';
    case 'openrouter':
      return 'O custo de cada resposta aparece no fim dela, como o OpenRouter cobrou.';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * Trocar o modelo e o esforço de uma conversa VIVA do OpenCode (F17).
 *
 * Não é o `Seletor` da barra, como no Claude e no Codex: o catálogo do
 * provedor de modelos tem centenas deles (D12), e o campo é o MESMO da tela de
 * Configurações e da de criar — `EscolhaDeModelo`, com a busca indo ao
 * servidor e os favoritos primeiro. O esforço é "Padrão do provedor" mais os
 * `variant` DAQUELE modelo (R7): o DeepSeek flash não tem `medium`, o Kimi não
 * tem nenhum. Num modal, e não num menu, porque são dois campos que dependem um
 * do outro e o dinheiro é da chave da frota — a troca merece um botão.
 *
 * **Pendente da API (costura S2):** `POST /sessoes/:id/modelo` ainda valida o
 * modelo contra o enum do Claude e recusa o id do OpenCode com `validacao`.
 * A tela está pronta; a recusa aparece pelo CÓDIGO, com uma frase que continua
 * verdadeira depois do conserto.
 */
export function ModalDeModeloOpencode({
  sessao,
  aoFechar,
}: {
  sessao: Sessao;
  aoFechar: () => void;
}): React.JSX.Element {
  const cliente = useQueryClient();
  const modeloAtual = sessao.modelo ?? '';
  const esforcoAtual = sessao.esforco ?? ESFORCO_PADRAO_OPENCODE;
  const [modelo, setModelo] = useState(modeloAtual);
  const [esforco, setEsforco] = useState(esforcoAtual);
  const [escolhido, setEscolhido] = useState<ModeloOpencode | null>(null);
  const [erro, setErro] = useState('');

  /*
    Os modelos que a tela conhece: a lista sem busca (favoritos primeiro) e o
    achado pela busca, que não está nela. É deles que saem os esforços — que
    são POR MODELO, e sumiriam sem o modelo inteiro em mãos.
  */
  const { data: lote } = usarCatalogo('');
  const conhecidos = useMemo(() => {
    const mapa = new Map<string, ModeloOpencode>();
    for (const m of lote?.modelos ?? []) mapa.set(m.id, m);
    if (escolhido) mapa.set(escolhido.id, escolhido);
    return mapa;
  }, [lote, escolhido]);
  const esforcos = opcoesDeEsforco(conhecidos.get(modelo), esforco);
  const mudou = modelo !== modeloAtual || esforco !== esforcoAtual;

  const trocar = useMutation({
    mutationFn: () =>
      chamar(`${ROTAS.sessoes}/${sessao.id}/modelo`, {
        method: 'POST',
        // `confirmacaoFable` é do Claude: nenhum modelo do OpenCode a pede.
        corpo: { modelo, esforco, confirmacaoFable: false },
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['sessao', sessao.id] });
      aoFechar();
    },
    onError: (e) =>
      setErro(
        mensagemDoErro(e, 'Não foi possível trocar o modelo.', {
          validacao: 'O servidor recusou este modelo para uma conversa do OpenCode.',
        }),
      ),
  });

  return (
    <Modal
      titulo="Modelo e esforço"
      aberto
      aoFechar={aoFechar}
      rodape={
        <>
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 transition hover:bg-zinc-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => trocar.mutate()}
            disabled={trocar.isPending || !mudou || modelo.length === 0}
            className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-sky-500 disabled:opacity-50"
          >
            {trocar.isPending ? 'trocando…' : 'Trocar'}
          </button>
        </>
      }
    >
      <div className="max-h-[55vh] space-y-3 overflow-y-auto" data-teste="modelo-do-opencode">
        <p className="text-xs text-zinc-500">
          Vale a partir do próximo turno. {fraseDoCusto(sessao.modelo ? provedorDoModelo(sessao.modelo) : null)}
        </p>
        <EscolhaDeModelo
          rotulo="Modelo"
          valor={modelo || null}
          conhecidos={conhecidos}
          aoEscolher={(novo) => {
            setModelo(novo.id);
            setEscolhido(novo);
            setErro('');
            setEsforco(esforcoAposTrocarModelo(esforco, novo));
          }}
        />
        {esforcos.length > 1 ? (
          <CampoDeEscolha
            rotulo="Esforço de raciocínio"
            valor={esforco}
            opcoes={esforcos}
            aoEscolher={(novo) => {
              setEsforco(novo);
              setErro('');
            }}
          />
        ) : (
          <p className="text-xs text-zinc-500" data-teste="sem-esforco">
            Este modelo não tem níveis de esforço: vale o padrão do provedor.
          </p>
        )}
        {erro ? <p className="text-sm text-red-300">{erro}</p> : null}
      </div>
    </Modal>
  );
}
